import React, { useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { CiWarning } from "react-icons/ci";
import { FaBuilding, FaCheck, FaCheckCircle } from "react-icons/fa";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { DeleteAllToken, GetDepartmentForCoordinator } from "../../../lib/api";
import Cookies from "js-cookie";
import { useQuery } from "@tanstack/react-query";

export const TokenReset = () => {
  const [open, setOpen] = useState(false);
  const [dialog, setDialog] = useState(0);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [resetType, setResetType] = useState(null);
  const [isResetting, setIsResetting] = useState(false);

  const {
    data: departmentData,
    isLoading,
    isPending
  } = useQuery({
    queryKey: ["GetDepartmentForCoordinator"],
    queryFn: () => GetDepartmentForCoordinator(),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset: resetForm,
  } = useForm();

  const handleClickOpen = () => {
    setDialog(1);
    setOpen(true);
  };

  const handleClose = () => {
    if (!isResetting) {
      setOpen(false);
      setDialog(0);
      setSelectedDepartments([]);
      setResetType(null);
      resetForm();
    }
  };

  const handleConfirmReset = () => {
    setDialog(2);
  };

  const handleResetTypeSelect = (type) => {
    setResetType(type);
    if (type === "current") {
      setDialog(3);
    } else {
      setDialog(4);
    }
  };

  const handleDepartmentToggle = (depId) => {
    setSelectedDepartments(prev => {
      if (prev.includes(depId)) {
        return prev.filter(id => id !== depId);
      } else {
        return [...prev, depId];
      }
    });
  };

  const handleSelectAll = () => {
    if (!departmentData?.data?.department) return;

    const allIds = departmentData.data.department.map(dept => dept.DepID.toString());

    if (selectedDepartments.length === allIds.length) {
      setSelectedDepartments([]);
    } else {
      setSelectedDepartments(allIds);
    }
  };

  const resetSingleDepartment = async (depId, depName) => {
    try {
      const response = await DeleteAllToken(depId);
      if (response.status === 200) {
        return { success: true, department: depName };
      } else {
        return { success: false, department: depName, error: "Failed to reset" };
      }
    } catch (error) {
      return { success: false, department: depName, error: error.message };
    }
  };

  const onSubmit = async (formData) => {
    if (isResetting) return;

    setIsResetting(true);
    const toastId = toast.loading("Resetting tokens...");

    try {
      let departmentsToReset = [];

      if (resetType === "current") {

        const currentDepId = Cookies.get("DepId");
        const currentDepName = "Current Department";
        departmentsToReset = [{ id: currentDepId, name: currentDepName }];
      } else if (resetType === "multiple") {

        departmentsToReset = selectedDepartments.map(depId => {
          const dept = departmentData?.data?.department?.find(
            d => d.DepID.toString() === depId
          );
          return {
            id: depId,
            name: dept ? dept.DepName : `Department ${depId}`
          };
        });
      }


      const results = [];
      let successCount = 0;
      let failCount = 0;

      for (let i = 0; i < departmentsToReset.length; i++) {
        const dept = departmentsToReset[i];


        toast.update(toastId, {
          render: `Resetting ${dept.name} (${i + 1}/${departmentsToReset.length})...`,
        });

        const result = await resetSingleDepartment(dept.id, dept.name);
        results.push(result);

        if (result.success) {
          successCount++;
        } else {
          failCount++;
        }


        if (i < departmentsToReset.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }


      toast.dismiss(toastId);

      if (successCount > 0 && failCount === 0) {
        toast.success(
          `Successfully reset tokens for ${successCount} department${successCount > 1 ? 's' : ''}`,
          { position: "top-right", closeOnClick: true }
        );
      } else if (successCount > 0 && failCount > 0) {
        toast.warning(
          `Reset ${successCount} department${successCount > 1 ? 's' : ''} successfully, but failed for ${failCount} department${failCount > 1 ? 's' : ''}`,
          { position: "top-right", closeOnClick: true }
        );
      } else {
        toast.error(
          `Failed to reset tokens for all ${failCount} department${failCount > 1 ? 's' : ''}`,
          { position: "top-right", closeOnClick: true }
        );
      }


    } catch (error) {
      toast.dismiss(toastId);
      console.error("Reset error:", error);
      toast.error("An error occurred during reset", {
        position: "top-right",
        closeOnClick: true,
      });
    } finally {
      setIsResetting(false);
      handleClose();
    }
  };

  const departments = departmentData?.data?.department || [];

  return (
    <React.Fragment>
      <Button
        className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:bg-gradient-to-bl font-medium rounded-lg text-sm px-5 py-2.5 text-center me-2 mb-2"
        style={{ color: "white", textTransform: "capitalize" }}
        variant="string"
        onClick={handleClickOpen}
        disabled={isResetting}
      >
        {isResetting ? "Resetting..." : "Reset Counter"}
      </Button>

      {/* Dialog 1: Initial Confirmation */}
      <Dialog open={open && dialog === 1} onClose={handleClose}>
        <DialogTitle className="flex flex-col items-center gap-2">
          <div className="p-3 bg-red-50 rounded-full">
            <CiWarning size={40} color="#dc2626" />
          </div>
          <span className="text-lg font-semibold">Reset Tokens</span>
        </DialogTitle>
        <DialogContent>
          <DialogContentText className="text-center text-gray-600">
            All current tokens will be cleared and numbering will start again from 1. <br></br>
            Do you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions className="justify-center gap-4 px-6 pb-6">
          <button
            className="px-5 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
            onClick={handleClose}
            disabled={isResetting}
          >
            Cancel
          </button>
          <button
            className="px-5 py-2.5 text-white bg-blue-600 hover:bg-blue-700 rounded-lg font-medium transition-colors disabled:bg-blue-400"
            onClick={handleConfirmReset}
            autoFocus
            disabled={isResetting}
          >
            Continue
          </button>
        </DialogActions>
      </Dialog>

      {/* Dialog 2: Reset Type Selection */}
      <Dialog open={open && dialog === 2} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle className="flex items-center gap-3">
          <FaBuilding className="text-blue-600" />
          <span className="text-lg font-semibold">Select Reset Scope</span>
        </DialogTitle>
        <DialogContent>
          <DialogContentText className="mb-6 text-gray-600">
            Choose what you want to reset:
          </DialogContentText>

          <div className="grid grid-cols-1 gap-4">
            <button
              className="flex items-center gap-4 p-4 text-left border border-gray-200 rounded-xl hover:border-blue-300 hover:bg-blue-50 transition-all disabled:opacity-50"
              onClick={() => handleResetTypeSelect("current")}
              disabled={isResetting}
            >
              <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 font-semibold">C</span>
              </div>
              <div className="flex-1">
                <div className="font-semibold text-gray-900">Current Department Only</div>
                <div className="text-sm text-gray-500 mt-1">
                  Reset token counter for your current department only
                </div>
              </div>
              <div className="text-blue-600">
                <FaCheckCircle />
              </div>
            </button>

            <button
              className="flex items-center gap-4 p-4 text-left border border-gray-200 rounded-xl hover:border-green-300 hover:bg-green-50 transition-all disabled:opacity-50"
              onClick={() => handleResetTypeSelect("multiple")}
              disabled={isResetting}
            >
              <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-green-600 font-semibold">A</span>
              </div>
              <div className="flex-1">
                <div className="font-semibold text-gray-900">Reset Tokens by Department</div>
                <div className="text-sm text-gray-500 mt-1">
                  Choose the departments for which the token counters need to be reset.
                  Only the selected departments will be affected.
                </div>
              </div>
              <div className="text-green-600">
                <FaCheckCircle />
              </div>
            </button>
          </div>
        </DialogContent>
        <DialogActions className="px-6 pb-6">
          <button
            className="px-5 py-2.5 text-gray-700 hover:text-gray-900 font-medium disabled:opacity-50"
            onClick={handleClose}
            disabled={isResetting}
          >
            Cancel
          </button>
        </DialogActions>
      </Dialog>

      {/* Dialog 4: Multi-Department Selection */}
      <Dialog open={open && dialog === 4} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle className="flex items-center justify-between border-b pb-4">
          <div className="flex items-center gap-3">
            <FaBuilding className="text-blue-600" />
            <div>
              <div className="text-lg font-semibold">Select Departments</div>
              <div className="text-sm text-gray-500">
                Select one or more departments to reset
              </div>
            </div>
          </div>
          {selectedDepartments.length > 0 && (
            <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
              {selectedDepartments.length} selected
            </span>
          )}
        </DialogTitle>

        <DialogContent className="!py-6">
          {isLoading || isPending ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <div className="mt-4 text-gray-600">Loading departments...</div>
            </div>
          ) : departments.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 text-4xl mb-4">🏢</div>
              <div className="text-gray-500">No departments available</div>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={handleSelectAll}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-500 rounded-lg disabled:opacity-50 b"
                  disabled={isResetting}
                >
                  <FaCheck className={`${selectedDepartments.length === departments.length ? 'text-blue-600' : 'text-gray-400'}`} />
                  {selectedDepartments.length === departments.length ? 'Deselect All' : 'Select All'}
                </button>
                <span className="text-sm text-gray-500">
                  {selectedDepartments.length} of {departments.length} selected
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-80 overflow-y-auto p-1">
                {departments.map((dept) => {
                  const isSelected = selectedDepartments.includes(dept.DepID.toString());
                  return (
                    <div
                      key={dept.DepID}
                      className={`p-4 rounded-xl border cursor-pointer transition-all ${isSelected
                        ? 'border-blue-300 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        } ${isResetting ? 'opacity-50 cursor-not-allowed' : ''}`}
                      onClick={() => !isResetting && handleDepartmentToggle(dept.DepID.toString())}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <div className={`w-3 h-3 rounded-full ${isSelected ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
                            <div className="font-semibold text-gray-900">{dept.DepName}</div>
                          </div>
                        </div>
                        {isSelected && (
                          <div className="text-blue-600">
                            <FaCheckCircle />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </DialogContent>

        <DialogActions className="border-t px-6 py-4">
          <div className="flex items-center justify-between w-full">
            <button
              className="px-5 py-2.5 text-gray-700 hover:text-gray-900 font-medium disabled:opacity-50"
              onClick={() => setDialog(2)}
              disabled={isResetting}
            >
              ← Back
            </button>
            <div className="flex gap-3">
              <button
                className="px-5 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium disabled:opacity-50"
                onClick={handleClose}
                disabled={isResetting}
              >
                Cancel
              </button>
              <button
                className={`px-5 py-2.5 rounded-lg font-medium transition-colors ${selectedDepartments.length > 0 && !isResetting
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                onClick={() => setDialog(3)}
                disabled={selectedDepartments.length === 0 || isResetting}
              >
                Continue ({selectedDepartments.length})
              </button>
            </div>
          </div>
        </DialogActions>
      </Dialog>

      {/* Dialog 3: Password Confirmation */}
      <Dialog open={open && dialog === 3} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle className="border-b pb-4">
          <div className="text-lg font-semibold">Confirm Reset</div>
          {resetType === "current" ? (
            <div className="text-sm text-gray-500 mt-1">Reset current department token counter</div>
          ) : (
            <div className="text-sm text-gray-500 mt-1">
              Reset {selectedDepartments.length} selected department{selectedDepartments.length !== 1 ? 's' : ''}
            </div>
          )}
        </DialogTitle>

        <DialogContent className="!py-6">
          {resetType === "multiple" && selectedDepartments.length > 0 && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg">
              <div className="font-medium text-gray-900 mb-2">Selected Departments ({selectedDepartments.length}):</div>
              <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                {selectedDepartments.map(depId => {
                  const dept = departments.find(d => d.DepID.toString() === depId);
                  return dept ? (
                    <span key={depId} className="px-3 py-1 bg-white border border-blue-200 rounded-full text-sm">
                      {dept.DepName}
                    </span>
                  ) : null;
                })}
              </div>
            </div>
          )}

          <DialogContentText className="mb-4 text-gray-600">
            Please enter your password to confirm the reset operation.
          </DialogContentText>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <input
                placeholder="Enter your password"
                {...register("password", {
                  required: "Password is required",
                  validate: (value) =>
                    value === Cookies.get("userPassword") ||
                    "Incorrect password",
                })}
                type="password"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors disabled:opacity-50"
                autoComplete="current-password"
                disabled={isResetting}
                autoFocus 
              />
              {errors.password && (
                <p className="mt-2 text-sm text-red-600">
                  {errors.password.message}
                </p>
              )}
            </div>
          </form>
        </DialogContent>

        <DialogActions className="border-t px-6 py-4">
          <div className="flex items-center justify-between w-full">
            <button
              className="px-5 py-2.5 text-gray-700 hover:text-gray-900 font-medium disabled:opacity-50"
              onClick={() => {
                if (resetType === "multiple") {
                  setDialog(4);
                } else {
                  setDialog(2);
                }
              }}
              disabled={isResetting}
            >
              ← Back
            </button>
            <div className="flex gap-3">
              <button
                className="px-5 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium disabled:opacity-50"
                onClick={handleClose}
                disabled={isResetting}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit(onSubmit)}
                className="px-5 py-2.5 text-white bg-red-600 hover:bg-red-700 rounded-lg font-medium transition-colors disabled:opacity-50"
                disabled={isResetting}
              >
                <div className="flex items-center gap-2">
                  <CiWarning />
                  <span>{isResetting ? "Resetting..." : "Confirm Reset"}</span>
                </div>
              </button>
            </div>
          </div>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};